/**
 * Client-side helpers for reading the quota object the generator API already
 * returns, so the UI can explain a 429 in place instead of flashing a toast.
 *
 * No backend change is involved: /api/generator returns the full quota both on
 * success (`data.quota`) and on a 429 (`meta.quota`, via createErrorResponse).
 */

export interface QuotaInfo {
  limit: number;
  used: number;
  remaining: number;
  /** ISO timestamp of the next UTC midnight reset. */
  resetsAtUtc: string;
  isAuthenticated: boolean;
}

/**
 * Generator allowances used by the quota gate's copy.
 *
 * These used to be read from NEXT_PUBLIC_GENERATOR_{GUEST,AUTH}_DAILY_LIMIT —
 * a mirror of the server's values that could (and did) drift: production never
 * set the server-side GENERATOR_GUEST_DAILY_LIMIT, so the real limit was the
 * code default of 3 while the public mirror was free to say anything.
 *
 * /api/generator now reports both numbers on every 429 (`guestLimit` /
 * `authLimit` for the requested scope, plus `generatorGuestLimit` for the
 * generator scope specifically, which the home gate's copy needs). Read them
 * from the response instead of from a parallel env var.
 */
export interface QuotaLimits {
  /** Guest allowance for the scope this response describes. */
  guestLimit: number | null;
  /** Signed-in allowance for the scope this response describes. */
  authLimit: number | null;
  /**
   * Guest allowance for the *generator* scope, regardless of which scope was
   * requested. The homepage gate points at the Generator, so it needs this
   * number rather than its own scope's.
   */
  generatorGuestLimit: number | null;
}

function readNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Pull the limit numbers out of an API payload, mirroring extractQuota's
 * envelope handling. Any missing field comes back null so callers degrade to
 * copy without a number rather than rendering "undefined".
 */
export function extractQuotaLimits(payload: unknown): QuotaLimits {
  const empty: QuotaLimits = {
    guestLimit: null,
    authLimit: null,
    generatorGuestLimit: null,
  };
  if (!payload || typeof payload !== "object") return empty;
  const root = payload as Record<string, unknown>;

  for (const key of ["meta", "details", "data"] as const) {
    const branch = root[key];
    if (branch && typeof branch === "object") {
      const b = branch as Record<string, unknown>;
      const found: QuotaLimits = {
        guestLimit: readNumber(b, "guestLimit"),
        authLimit: readNumber(b, "authLimit"),
        generatorGuestLimit: readNumber(b, "generatorGuestLimit"),
      };
      if (
        found.guestLimit !== null ||
        found.authLimit !== null ||
        found.generatorGuestLimit !== null
      ) {
        return found;
      }
    }
  }

  return {
    guestLimit: readNumber(root, "guestLimit"),
    authLimit: readNumber(root, "authLimit"),
    generatorGuestLimit: readNumber(root, "generatorGuestLimit"),
  };
}

/**
 * The server encodes "no limit" as a sentinel number rather than null — 9999
 * for the unlimited home scope, Number.MAX_SAFE_INTEGER for Premium accounts.
 * Anything at or above this threshold must never be rendered as a count.
 */
export const UNLIMITED_REMAINING_THRESHOLD = 1000;

/** True when `remaining` is a sentinel meaning "unlimited", not a real count. */
export function isUnlimitedRemaining(remaining: number | null): boolean {
  return remaining !== null && remaining >= UNLIMITED_REMAINING_THRESHOLD;
}

function isQuotaInfo(value: unknown): value is QuotaInfo {
  if (!value || typeof value !== "object") return false;
  const q = value as Record<string, unknown>;
  return (
    typeof q.limit === "number" &&
    typeof q.used === "number" &&
    typeof q.remaining === "number" &&
    typeof q.resetsAtUtc === "string" &&
    typeof q.isAuthenticated === "boolean"
  );
}

/**
 * Pull the quota object out of an API payload. Handles the success envelope
 * (`data.quota`), the error envelope (`meta.quota`) and a `details.quota`
 * shape, so it keeps working if the envelope key ever changes.
 */
export function extractQuota(payload: unknown): QuotaInfo | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;

  for (const key of ["meta", "details", "data"] as const) {
    const branch = root[key];
    if (branch && typeof branch === "object") {
      const quota = (branch as Record<string, unknown>).quota;
      if (isQuotaInfo(quota)) return quota;
    }
  }

  if (isQuotaInfo(root.quota)) return root.quota;
  return null;
}

/**
 * Relative countdown to the reset, e.g. "6h 12m" or "42m".
 * Hours and minutes are both shown so the wait reads as a concrete, finite
 * number rather than a vague "6h". The trailing "0m" is dropped on the hour.
 * Returns null when the reset time is unusable or already past.
 */
export function formatResetIn(
  resetsAtUtc: string,
  now: number = Date.now(),
): string | null {
  const resetAt = Date.parse(resetsAtUtc);
  if (Number.isNaN(resetAt)) return null;

  const diffMs = resetAt - now;
  if (diffMs <= 0) return null;

  const minutes = Math.ceil(diffMs / 60000);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}
